import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { debounce, type DebouncedFunc } from 'lodash';

export { type DebouncedFunc };

class DebounceInstaller<T extends (...args: never[]) => unknown> {
  private debounced: DebouncedFunc<T> | null = null;
  private pendingArgs: Parameters<T> | null = null;
  readonly wrapper: DebouncedFunc<T>;

  constructor() {
    const invoke = (...args: Parameters<T>): ReturnType<T> | undefined => {
      const current = this.debounced;
      if (current === null) {
        this.pendingArgs = args;
        return undefined;
      }
      return current(...args);
    };
    invoke.cancel = () => {
      this.pendingArgs = null;
      const current = this.debounced;
      if (current !== null) {
        current.cancel();
      }
    };
    invoke.flush = () => {
      const current = this.debounced;
      if (current === null) {
        throw new Error('useDebounce flush called before the debounced function was installed');
      }
      return current.flush();
    };
    this.wrapper = invoke;
  }

  install(next: DebouncedFunc<T> | null) {
    this.debounced = next;
    const pending = this.pendingArgs;
    if (next !== null && pending !== null) {
      this.pendingArgs = null;
      next(...pending);
    }
  }
}

export function useDebounce<
  T extends (
    ...args: any // oxlint-disable-line typescript/no-explicit-any
  ) => any, // oxlint-disable-line typescript/no-explicit-any
>(callback: T, delay: number): DebouncedFunc<T> {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  const [installer] = useState(() => new DebounceInstaller<T>());

  useLayoutEffect(() => {
    function forwarded(...args: Parameters<T>): ReturnType<T> {
      return callbackRef.current(...args);
    }
    const next = debounce(forwarded, delay);
    installer.install(next);
    return () => {
      next.cancel();
      installer.install(null);
    };
  }, [installer, delay]);

  return installer.wrapper;
}
